import { Fragment, type MouseEvent, type ReactNode } from "react";

import clsx from "clsx";
import { saveAs } from "file-saver";
import Papa from "papaparse";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { UpdateTileModal } from "@features/overview-v2/components";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { TileType, type TileDescriptor } from "@features/overview-v2/types";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useFeatureFlags } from "@hooks/http";
import { useDisclosure } from "@hooks/use-disclosure";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

import { tileToExportData } from "./utils";

export interface Props {
  readonly tile: TileDescriptor;
  readonly metric1?: SalesPageMetric;
  readonly metric2?: SalesPageMetric;
  readonly metric1Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
  readonly metric2Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
  readonly showDeleteOnly?: boolean;
  readonly onDelete: () => void;
}

export const TileEllipsisMenu = ({
  tile,
  metric1,
  metric2,
  metric1Data,
  metric2Data,
  showDeleteOnly,
  onDelete,
}: Props) => {
  const { user } = useUser();

  const { hasFeatureFlag } = useFeatureFlags();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();

  const prettyLogEventPage = usePrettyLogEventPage();

  const { cloneTile, updateTiles } = useOverviewContext();

  return (
    <>
      <Menu as="div" className="w-8 h-8 z-[10]">
        <MenuButton as={Fragment}>
          {({ active }) => (
            <button
              className={clsx(
                "flex items-center justify-center rounded-full p-0 h-8 w-8",
                {
                  "text-white bg-cosmic hover:bg-cosmic": active,
                  "text-gray-1 hover:bg-cosmic-5 hover:text-cosmic": !active,
                },
              )}
              onMouseDown={(e: MouseEvent) => e.stopPropagation()}
            >
              <i
                className={clsx("fa-regular fa-ellipsis text-sm", {
                  "text-white": active,
                })}
              />
            </button>
          )}
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <MenuItems
            className={clsx(
              "z-[10] w-[240px] bg-v2-primary rounded-lg elevation-1 text-sm py-1",
            )}
            onMouseDown={(e) => e.stopPropagation()}
            anchor="bottom end"
          >
            {!showDeleteOnly && (
              <>
                <Item onClick={() => openModal()}>
                  <i
                    className={clsx(
                      "fa-regular fa-sharp fa-cog text-cosmic text-sm mr-2 w-4",
                      "flex items-center justify-center",
                    )}
                  />
                  Tile settings
                </Item>
                {tile.breakdowns.length > 0 && (
                  <>
                    <Item
                      onClick={() => {
                        updateTiles(
                          {
                            id: tile.id,
                            update: {
                              sortBreakdowns: "value",
                            },
                          },
                          {
                            successMessage: "Tile successfully updated.",
                            errorMessage:
                              "There was an error updating the tile.",
                          },
                        );
                      }}
                    >
                      <i
                        className={clsx(
                          "fa-sort text-cosmic text-sm mr-2 w-4",
                          "flex items-center justify-center",
                          {
                            "fa-regular": tile.sortBreakdowns === "name",
                            "fa-solid": tile.sortBreakdowns !== "name",
                          },
                        )}
                      />
                      Sort by metric (high to low)
                    </Item>
                    <Item
                      onClick={() => {
                        updateTiles(
                          {
                            id: tile.id,
                            update: {
                              sortBreakdowns: "name",
                            },
                          },
                          {
                            successMessage: "Tile successfully updated.",
                            errorMessage:
                              "There was an error updating the tile.",
                          },
                        );
                      }}
                    >
                      <i
                        className={clsx(
                          "fa-sort text-cosmic text-sm mr-2 w-4",
                          "flex items-center justify-center",
                          {
                            "fa-regular": tile.sortBreakdowns !== "name",
                            "fa-solid": tile.sortBreakdowns === "name",
                          },
                        )}
                      />
                      Sort A to Z
                    </Item>
                  </>
                )}
                <Item
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Tile",
                      action: "onClick",
                      additionalActionInfo: "Clone tile",
                      data: {
                        pageContext: { tile },
                      },
                    });
                    cloneTile(tile);
                  }}
                >
                  <i
                    className={clsx(
                      "fa-regular fa-sharp fa-clone text-cosmic text-sm mr-2 w-4",
                      "flex items-center justify-center",
                    )}
                  />
                  Duplicate
                </Item>
                {hasFeatureFlag("enableTileDataExport") &&
                  metric1 &&
                  metric1Data && (
                    <Item
                      onClick={() => {
                        prettyLogEventPage({
                          domain: "Tile",
                          action: "onClick",
                          additionalActionInfo: "Export tile data",
                          data: {
                            pageContext: { tile },
                          },
                        });

                        const result = tileToExportData({
                          tile,
                          metric1,
                          metric2,
                          metric1Data,
                          metric2Data,
                          timezone: user.timezone,
                        });

                        let defaultName =
                          metric1.metadata.overviewPageMetricName ||
                          metric1.name;

                        if (tile.type === TileType.CONVERSION_LAG) {
                          defaultName += " Conversion Lag";
                        }

                        const name = tile.name || defaultName;

                        const csvString = Papa.unparse(result);
                        const blob = new Blob([csvString], {
                          type: "text/plain;charset=utf-8",
                        });
                        saveAs(blob, `north-beam-tile-data-${name}.csv`);
                      }}
                    >
                      <i
                        className={clsx(
                          "fa-regular fa-download text-cosmic text-sm mr-2 w-4",
                          "flex items-center justify-center",
                        )}
                      />
                      Export tile data
                    </Item>
                  )}
              </>
            )}
            <Item
              onClick={() => {
                prettyLogEventPage({
                  domain: "Tile",
                  action: "onClick",
                  additionalActionInfo: "Delete tile",
                  data: {
                    pageContext: { tile },
                  },
                });
                onDelete();
              }}
            >
              <i
                className={clsx(
                  "fa-regular fa-trash text-mars text-sm mr-2 w-4",
                  "flex items-center justify-center",
                )}
              />
              Delete
            </Item>
          </MenuItems>
        </Transition>
      </Menu>
      {modalOpened && (
        <UpdateTileModal
          isOpen={modalOpened}
          tile={tile}
          onClose={() => closeModal()}
        />
      )}
    </>
  );
};

const Item = ({
  onClick,
  children,
}: {
  onClick(): void;
  children: ReactNode;
}) => (
  <MenuItem>
    {({ focus }) => (
      <button
        className={clsx(
          "text-v2-primary no-underline pl-3 pr-4 h-8 w-full",
          "flex items-center cursor-pointer whitespace-nowrap",
          { "bg-cosmic-4": focus },
        )}
        onClick={onClick}
      >
        {children}
      </button>
    )}
  </MenuItem>
);
